import { Grid, Typography } from "@mui/material"
import GlobalButton from "../../../../Utils/GlobalUtilsComponents/GlobalButton"
import tableIcon from "../../../../assets/Images/tableIcon.svg"
import styles from "../../../../Styles/ComponentStyles/ProfileStyle/AnnuityProfilesStyle.module.scss"
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import { useSearchParams } from "react-router-dom";

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

function formatNumber(number: any) {
    if (Number.isNaN(number) || !Number.isFinite(number)) {
        return number;
    }
    const formattedNumber = number.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return number < 0 ? `-$${formattedNumber.slice(1)}` : `$${formattedNumber}`;
}

const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            display: true,
            position: 'top' as const,
            align: "end" as const,
            labels: {
                color: '#6B7280',
                font: { size: 12, weight: "500" },
                usePointStyle: true,
                pointStyle: "circle" as const,
                boxWidth: 8,
                boxHeight: 8,
                generateLabels: function (chart: any) {
                    const datasets = chart.data.datasets;
                    const defaultLabels = datasets
                        .filter((dataset: any) => dataset.label !== "Surplus or Deficit")
                        .map((dataset: any, i: number) => ({
                            text: dataset.label,
                            fillStyle: dataset.backgroundColor,
                            strokeStyle: dataset.backgroundColor,
                            lineWidth: 0,
                            hidden: false,
                            index: i
                        }));

                    // Add custom surplus and deficit labels
                    return [
                        ...defaultLabels,
                        {
                            text: 'Surplus',
                            fillStyle: 'rgba(0, 154, 92, 1)',
                            strokeStyle: 'rgba(0, 154, 92, 1)',
                            lineWidth: 0,
                            hidden: false,
                            index: defaultLabels.length
                        },
                        {
                            text: 'Deficit',
                            fillStyle: 'rgba(232, 22, 22, 1)',
                            strokeStyle: 'rgba(232, 22, 22, 1)',
                            lineWidth: 0,
                            hidden: false,
                            index: defaultLabels.length + 1
                        }
                    ];
                }
            }
        },
        title: {
            display: false,
        },
        tooltip: {
            callbacks: {
                label: (context: any) => {
                    const datasetLabel = context.dataset.label;
                    const value = context.parsed.y;

                    if (datasetLabel === "Surplus or Deficit") {
                        return value >= 0
                            ? `Surplus: ${formatNumber(value)}`
                            : `Deficit: ${formatNumber(value)}`;
                    }
                    if (datasetLabel === "Total Annual Income") {
                        return `Total Annual Income: ${formatNumber(value)}`;
                    }
                    if (datasetLabel === "Desired Annual Income") {
                        return `Desired Annual Income: ${formatNumber(value)}`;
                    }
                    return `${datasetLabel}: ${value}`;
                },
                titleFont: { size: 18 },
                bodyFont: { size: 16 },
            }
        }
    },
    scales: {
        x: {
            grid: { display: false },
            ticks: { color: '#6B7280', font: { size: 12 } },
            border: { display: false }
        },
        y: {
            grid: {
                color: 'rgba(148, 163, 184, 0.4)',
                borderDash: [4, 4],
            },
            border: { display: false },
            ticks: {
                color: '#6B7280',
                font: { size: 12 },
                callback: function (value: any) {
                    return "$" + value?.toLocaleString();
                }
            },
        },
    },
};

const AnnuityIncomeOutputChart = ({ chartData }: any) => {
    const [searchParams, setSearchParams] = useSearchParams();

    const toggleChart = () => {
        if (searchParams.get("showChart") === "true") {
            searchParams.delete("showChart");
        } else {
            searchParams.set("showChart", "true");
        }
        setSearchParams(searchParams);
    };

    const isValidChartData = chartData?.labels && chartData?.datasets && Array.isArray(chartData.datasets);

    const data = {
        labels: chartData?.labels,
        datasets: [
            {
                label: "Total Annual Income",
                backgroundColor: "rgba(243, 184, 170, 1)",
                data: chartData?.datasets?.[0]?.data
            },
            {
                label: "Desired Annual Income",
                backgroundColor: "rgba(245, 206, 164, 1)",
                data: chartData?.datasets?.[1]?.data
            },
            {
                label: 'Surplus or Deficit',
                backgroundColor: (context: any) => {
                    const value = context.raw;
                    return value >= 0 ? 'rgba(0, 154, 92, 1)' : 'rgba(232, 22, 22, 1)';
                },
                data: chartData?.datasets?.[2]?.data,
            },
        ]
    };

    return (
        <Grid container direction={"column"} gap={"15px"} className={styles.chartOuterContainer}>
            <Typography className={styles.outputTitle}>Annuity - Income</Typography>
            <Grid item sx={{ alignSelf: "flex-end" }}>
                <GlobalButton
                    className={styles.chartBtn}
                    startIcon={<img src={tableIcon} alt="" />}
                    value="Table"
                    onClick={toggleChart}
                />
            </Grid>
            <Grid item xs={7} className={styles.globalBlurWrapperForOutputforIncomeChart}>
                <Grid container className={styles.chartCanvasWrapper}>
                    {isValidChartData ? (
                        <Bar options={options} data={data} />
                    ) : (
                        <Typography variant="body1" color={"#475569"}>
                            Chart is not available.
                        </Typography>
                    )}
                </Grid>
            </Grid>
        </Grid>
    );
};

export default AnnuityIncomeOutputChart;